import { Product, SaleProperty } from '@trwl/backend.api';
import { $setVariant } from '@trwl/store/product';
import cn from 'classnames';
import { useUnit } from 'effector-react';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

export interface IVariantSelector {
  product: Product;
  onSizeGridClick?: () => void;
}

const propertyNameTranslations: Record<string, string> = Object.freeze({
  size: 'Размер',
  color: 'Цвет',
});

// eslint-disable-next-line react/display-name
const PropertyButton = memo(
  ({
    item,
    isSelected,
    onClick,
  }: {
    item: SaleProperty;
    isSelected: boolean;
    onClick: () => void;
  }) => (
    <div
      style={{ borderWidth: '1.5px' }}
      className={cn(
        'flex items-center justify-center cursor-pointer w-full duration-300',
        'border-[1.6px] rounded-[5px] p-2 text-[16px] box-border',
        isSelected
          ? 'border-[#282828] text-[#282828]'
          : 'border-[rgba(0,0,0,0.1)] text-[rgba(0,0,0,0.6)]',
      )}
      onClick={() => {
        if (!isSelected) onClick();
      }}
    >
      {item.propertyValue}
    </div>
  ),
);

export const VariantSelector = ({
  product,
  onSizeGridClick,
}: IVariantSelector) => {
  const setVariant = useUnit($setVariant);

  const [selectedProperties, setSelectedProperties] = useState<{
    [key: string]: SaleProperty;
  }>({});

  const saleProperties = useMemo(() => {
    const result = new Map<string, SaleProperty[]>();
    const usedProperties = new Map<string, Set<string>>();
    product.variants
      .filter((variant) => variant.stock > 0)
      .forEach((variant) => {
        variant.saleProperties.forEach((property) => {
          if (!result.has(property.propertyName)) {
            result.set(property.propertyName, []);
            usedProperties.set(property.propertyName, new Set());
          }

          if (
            usedProperties
              .get(property.propertyName)
              ?.has(property.propertyValue) ||
            result.get(property.propertyName)?.includes(property)
          )
            return;

          usedProperties
            .get(property.propertyName)
            ?.add(property.propertyValue);
          result.get(property.propertyName)?.push(property);
        });
      });
    result.forEach((values) => {
      values.sort((a, b) => a.sort - b.sort);
    });
    return result;
  }, [product.variants]);

  const handlePropertySelection = useCallback(
    (propertyName: string, propertyValue: SaleProperty) => {
      const newProperties = {
        ...selectedProperties,
        [propertyName]: propertyValue,
      };
      setSelectedProperties(newProperties);
      const selectedVariant = product.variants?.find((variant) => {
        const keys = new Set(
          variant.saleProperties.map((prop) => prop.propertyName),
        );
        if (!Object.keys(newProperties).every((key) => keys.has(key)))
          return false;

        return variant.saleProperties.every(
          (prop) =>
            newProperties[prop.propertyName] &&
            newProperties[prop.propertyName].propertyValue ===
              prop.propertyValue,
        );
      });
      setVariant(selectedVariant || null);
    },
    [product.variants, selectedProperties, setVariant],
  );

  useEffect(() => {
    const sortedVariants = product.variants.sort((a, b) => a.price - b.price);
    const firstVariant = sortedVariants[0];

    if (!firstVariant) {
      setSelectedProperties({});
      setVariant(null);
      return;
    }

    setVariant(firstVariant);
    const properties = Object.fromEntries(
      firstVariant.saleProperties.map((saleProperty) => [
        saleProperty.propertyName,
        saleProperty,
      ]),
    );
    setSelectedProperties(properties);
  }, [product.variants, setVariant, setSelectedProperties]);

  const postfix = useCallback(
    (propertyValues: SaleProperty[]) => {
      const equals = (a: string, b: string) => {
        return a.trim() === b.trim();
      };
      const sizeGrid = Object.fromEntries(
        product.sizeGrid.map(({ key, value }) => [key, value.split(',')]),
      );
      const sizeGridScores = Object.fromEntries(
        Object.keys(sizeGrid).map((key) => [key, 0]),
      );
      for (const [key, values] of Object.entries(sizeGrid)) {
        propertyValues.forEach((propertyValue) => {
          values.forEach((value) => {
            if (equals(value, propertyValue.propertyValue)) {
              sizeGridScores[key]++;
            }
          });
        });
      }

      const metricSystems = Object.entries(sizeGridScores).sort(
        (a, b) => b[1] - a[1],
      );
      if (
        metricSystems.length > 2 &&
        metricSystems[0][1] === metricSystems[1][1]
      ) {
        return '';
      }
      return ' ' + metricSystems[0][0];
    },
    [product.sizeGrid],
  );

  const isSelected = useCallback((o1: SaleProperty, o2: SaleProperty) => {
    return (
      o1 !== undefined &&
      o2 !== undefined &&
      o1.propertyValue === o2.propertyValue &&
      o2.propertyName === o2.propertyName
    );
  }, []);

  return (
    <>
      {Array.from(saleProperties.entries()).map(
        ([propertyName, propertyValues], index) => {
          if (propertyValues.length <= 1 && index > 0) return null;
          return (
            <div
              key={propertyName}
              className="w-full flex flex-col gap-3 relative"
            >
              {product.sizeGrid.length && index === 0 && (
                <div className={'z-20 top-[14px] right-0 absolute'}>
                  <div
                    className="text-zinc-600 underline cursor-pointer text-[18px]"
                    onClick={onSizeGridClick}
                  >
                    размерная сетка
                  </div>
                </div>
              )}
              <div className="text-xl">
                {propertyNameTranslations[propertyName.toLowerCase()] ||
                  propertyName}
                {propertyName === 'Size' && postfix(propertyValues)}
              </div>
              <div className="grid grid-cols-4 gap-2">
                {propertyValues.map((item) => (
                  <PropertyButton
                    key={item.id}
                    item={item}
                    isSelected={isSelected(
                      selectedProperties[propertyName],
                      item,
                    )}
                    onClick={() => handlePropertySelection(propertyName, item)}
                  />
                ))}
              </div>
            </div>
          );
        },
      )}
    </>
  );
};
